import React, { useEffect, useState } from 'react';
import { Flame } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Cache module-level pour éviter de fetcher plusieurs fois la même liste
let _CAS_CACHE = null;
let _CAS_PROMISE = null;

const fetchCasDoubleUsage = async () => {
  if (_CAS_CACHE) return _CAS_CACHE;
  if (_CAS_PROMISE) return _CAS_PROMISE;
  const token = localStorage.getItem('token');
  _CAS_PROMISE = fetch(`${API_URL}/api/cas-double-usage-ticgn`, {
    headers: { 'Authorization': `Bearer ${token}` },
  })
    .then((r) => (r.ok ? r.json() : []))
    .then((data) => {
      _CAS_CACHE = Array.isArray(data) ? data : [];
      _CAS_PROMISE = null;
      return _CAS_CACHE;
    })
    .catch(() => {
      _CAS_PROMISE = null;
      _CAS_CACHE = [];
      return _CAS_CACHE;
    });
  return _CAS_PROMISE;
};

export const invalidateCasDoubleUsageCache = () => {
  _CAS_CACHE = null;
  _CAS_PROMISE = null;
};

/**
 * Double usage TICGN — case à cocher + cas réglementaire (référentiel admin) +
 * justification libre + ratio (% ou MWh). Génère automatiquement une tranche
 * EXO_DOUBLE_USAGE côté backend.
 */
const DoubleUsageBlock = ({ value, onChange, testId = 'du' }) => {
  const v = value || { active: false, cas_code: '', cas_libelle: '', raison: '', mode: 'ratio', valeur: '' };
  const [cas, setCas] = useState([]);

  useEffect(() => {
    let mounted = true;
    fetchCasDoubleUsage().then((list) => { if (mounted) setCas(list); });
    return () => { mounted = false; };
  }, []);

  const update = (patch) => onChange({ ...v, ...patch });

  const handleCasChange = (code) => {
    const found = cas.find((c) => c.code === code);
    update({ cas_code: code, cas_libelle: found ? found.libelle : '' });
  };

  return (
    <div className="border border-dashed border-amber-300 rounded-lg p-3 mb-2 bg-amber-50/40">
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={!!v.active}
          onChange={(e) => update({ active: e.target.checked })}
          className="w-4 h-4 text-amber-600 border-amber-400 rounded"
          data-testid={`double-usage-toggle-${testId}`}
        />
        <span className="text-sm font-medium text-amber-900 flex items-center gap-1">
          <Flame className="w-4 h-4" /> Ce PCE comporte un double usage (exonération)
        </span>
      </label>
      {v.active && (
        <div className="mt-3 space-y-3">
          <div>
            <label className="block text-xs text-amber-900 mb-1">Cas réglementaire de double usage *</label>
            <select
              value={v.cas_code || ''}
              onChange={(e) => handleCasChange(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-amber-300 rounded-lg bg-white"
              data-testid={`double-usage-cas-${testId}`}
            >
              <option value="">-- Sélectionner un cas --</option>
              {cas.map((c) => (
                <option key={c.id || c.code} value={c.code}>
                  {c.ordre ? `${c.ordre}° ` : ''}{c.libelle}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-amber-800 mt-1">
              Référentiel art. 265 nonies du CdD. Ajoutez vos cas spécifiques dans Admin → TICGN → Cas Double Usage.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <label className="block text-xs text-amber-900 mb-1">Justification complémentaire (optionnel)</label>
              <input
                type="text"
                value={v.raison || ''}
                onChange={(e) => update({ raison: e.target.value })}
                placeholder="Précisions internes (procédé, équipement, etc.)"
                className="w-full px-3 py-2 text-sm border border-amber-300 rounded-lg bg-white"
                data-testid={`double-usage-raison-${testId}`}
              />
            </div>
            <div>
              <label className="block text-xs text-amber-900 mb-1">Mode de saisie</label>
              <div className="flex gap-2 mb-1">
                <label className="inline-flex items-center gap-1 text-xs">
                  <input
                    type="radio"
                    checked={v.mode !== 'mwh'}
                    onChange={() => update({ mode: 'ratio' })}
                    data-testid={`double-usage-mode-ratio-${testId}`}
                  /> Ratio (%)
                </label>
                <label className="inline-flex items-center gap-1 text-xs">
                  <input
                    type="radio"
                    checked={v.mode === 'mwh'}
                    onChange={() => update({ mode: 'mwh' })}
                    data-testid={`double-usage-mode-mwh-${testId}`}
                  /> MWh
                </label>
              </div>
              <input
                type="number"
                step="0.01"
                min="0"
                value={v.valeur ?? ''}
                onChange={(e) => update({ valeur: e.target.value })}
                placeholder={v.mode === 'mwh' ? 'ex. 120' : 'ex. 35 (=35 %)'}
                className="w-full px-3 py-2 text-sm border border-amber-300 rounded-lg bg-white"
                data-testid={`double-usage-valeur-${testId}`}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DoubleUsageBlock;
